import React from "react";

/**
 * TRADITIONAL East-Indian (Bengali / Odia) Vedic Kundli chart.
 * ────────────────────────────────────────────────────────────────
 * FIXED-RASHI CHART:
 *   • Each RASHI has a permanent physical position — never moves.
 *   • HOUSE numbers are computed dynamically from the Ascendant.
 *   • Lagna marker sits INSIDE the ascendant-rashi's fixed cell.
 *
 * FIXED RASHI POSITIONS (per the user's authoritative reference):
 *    1 Aries   / Mesha       = TOP CENTER (TM edge)
 *    2 Taurus  / Vrishabha   = TOP-LEFT INNER triangle  (upper-right of TL cell)
 *    3 Gemini  / Mithuna     = TOP-LEFT OUTER triangle  (lower-left of TL cell)
 *    4 Cancer  / Karka       = LEFT CENTER (ML edge)
 *    5 Leo     / Simha       = BOTTOM-LEFT INNER triangle (upper-left of BL cell)
 *    6 Virgo   / Kanya       = BOTTOM-LEFT OUTER triangle (lower-right of BL cell)
 *    7 Libra   / Tula        = BOTTOM CENTER (BM edge)
 *    8 Scorpio / Vrishchika  = BOTTOM-RIGHT OUTER triangle (lower-left of BR cell)
 *    9 Sagit.  / Dhanu       = BOTTOM-RIGHT INNER triangle (upper-right of BR cell)
 *   10 Capri.  / Makara      = RIGHT CENTER (MR edge)
 *   11 Aquar.  / Kumbha      = TOP-RIGHT OUTER triangle (lower-right of TR cell)
 *   12 Pisces  / Meena       = TOP-RIGHT INNER triangle (upper-left of TR cell)
 *
 * Corner-cell diagonals all go from OUTER-chart-corner to INNER-chart-corner
 * (they converge toward the centre "Rasi" square).
 */

const RASHI_NAMES = [
  "Mesha", "Vrishabha", "Mithuna", "Karka", "Simha", "Kanya",
  "Tula", "Vrishchika", "Dhanu", "Makara", "Kumbha", "Meena",
];
const RASHI_SHORT = ["Ar","Ta","Ge","Ca","Le","Vi","Li","Sc","Sg","Cp","Aq","Pi"];
const PLANET_SHORT = {
  Sun:"Su", Moon:"Mo", Mars:"Ma", Mercury:"Me", Jupiter:"Ju",
  Venus:"Ve", Saturn:"Sa", Rahu:"Ra", Ketu:"Ke",
};

// viewBox 300×300. Each cell = 100×100. Corner cells have diagonals.
// For each RASHI (1..12): centroid + label positions inside its own triangle/cell.
const RASHI_POS = {
  1:  { cx: 150, cy: 52,  hLx: 108, hLy: 12,  rLx: 192, rLy: 12  },  // Aries - TM
  2:  { cx: 67,  cy: 32,  hLx: 4,   hLy: 12,  rLx: 92,  rLy: 12  },  // Taurus - TL inner (upper-right)
  3:  { cx: 33,  cy: 68,  hLx: 4,   hLy: 96,  rLx: 60,  rLy: 96  },  // Gemini - TL outer (lower-left)
  4:  { cx: 50,  cy: 150, hLx: 4,   hLy: 112, rLx: 92,  rLy: 112 },  // Cancer - ML
  5:  { cx: 33,  cy: 232, hLx: 4,   hLy: 212, rLx: 60,  rLy: 212 },  // Leo - BL inner (upper-left)
  6:  { cx: 67,  cy: 268, hLx: 4,   hLy: 293, rLx: 92,  rLy: 293 },  // Virgo - BL outer (lower-right)
  7:  { cx: 150, cy: 250, hLx: 108, hLy: 293, rLx: 192, rLy: 293 },  // Libra - BM
  8:  { cx: 233, cy: 268, hLx: 208, hLy: 293, rLx: 296, rLy: 293 },  // Scorpio - BR outer (lower-left)
  9:  { cx: 267, cy: 232, hLx: 240, hLy: 212, rLx: 296, rLy: 212 },  // Sagit - BR inner (upper-right)
  10: { cx: 250, cy: 150, hLx: 208, hLy: 112, rLx: 296, rLy: 112 },  // Capri - MR
  11: { cx: 267, cy: 68,  hLx: 240, hLy: 96,  rLx: 296, rLy: 96  },  // Aquar - TR outer (lower-right)
  12: { cx: 233, cy: 32,  hLx: 208, hLy: 12,  rLx: 296, rLy: 12  },  // Pisces - TR inner (upper-left)
};

// Lagna-triangle polygon points (for amber highlight) per rashi
const RASHI_POLY = {
  1:  "100,0 200,0 200,100 100,100",         // TM cell rectangle
  2:  "0,0 100,0 100,100",                    // TL upper-right triangle
  3:  "0,0 100,100 0,100",                    // TL lower-left triangle
  4:  "0,100 100,100 100,200 0,200",          // ML cell
  5:  "0,200 100,200 0,300",                  // BL upper-left triangle
  6:  "100,200 100,300 0,300",                // BL lower-right triangle
  7:  "100,200 200,200 200,300 100,300",      // BM cell
  8:  "200,200 200,300 300,300",              // BR lower-left triangle
  9:  "200,200 300,200 300,300",              // BR upper-right triangle
  10: "200,100 300,100 300,200 200,200",      // MR cell
  11: "300,100 200,100 300,0",                // TR lower-right triangle... wait
  12: "200,0 300,0 200,100",                  // TR upper-left triangle
};
// Correct TR triangles based on "/" diagonal from (300,0) to (200,100):
//   Upper-left triangle (Pisces 12):  (200,0), (300,0), (200,100)
//   Lower-right triangle (Aquarius 11): (300,0), (300,100), (200,100)
RASHI_POLY[11] = "300,0 300,100 200,100";
RASHI_POLY[12] = "200,0 300,0 200,100";

const houseOfRashi = (ascRashi, rashi) => ((rashi - ascRashi + 12) % 12) + 1;

function validateKundli(k, ascRashi) {
  if (!k) return "No kundli data";
  if (!Number.isInteger(ascRashi) || ascRashi < 1 || ascRashi > 12)
    return `Ascendant rashi must be 1..12, got ${ascRashi}`;
  // All 12 rashi positions must be defined
  for (let r = 1; r <= 12; r++)
    if (!RASHI_POS[r]) return `Missing fixed position for rashi ${r}`;
  // House map bijection
  const houses = new Set();
  for (let r = 1; r <= 12; r++) houses.add(houseOfRashi(ascRashi, r));
  if (houses.size !== 12) return "House mapping is not a bijection";
  if (houseOfRashi(ascRashi, ascRashi) !== 1) return "Ascendant rashi must be House 1";
  return null;
}

export default function EastIndianChart({ kundli, showRashiName = false }) {
  if (!kundli) return null;
  const ascSignIndex =
    kundli.ascendant?.sign_index ??
    (typeof kundli.ascendant?.longitude === "number"
      ? Math.floor(kundli.ascendant.longitude / 30)
      : 0);
  const ascRashi = ascSignIndex + 1;
  const err = validateKundli(kundli, ascRashi);
  if (err) return (
    <div className="p-4 rounded-xl border-2 border-red-500 bg-red-50 text-red-700 text-sm"
         data-testid="east-indian-chart-error">
      <strong>Validation failed:</strong> {err}
    </div>
  );

  // Group planets by RASHI (fixed position)
  const planetsByRashi = {};
  (kundli.planets || []).forEach((p) => {
    const r = (p.sign_index ?? 0) + 1;
    (planetsByRashi[r] = planetsByRashi[r] || []).push(p);
  });

  const STROKE = "#0F172A";

  return (
    <div className="flex justify-center">
      <svg viewBox="0 0 300 300" width="440" height="440"
           className="rounded-xl bg-white dark:bg-slate-900 shadow-xl"
           data-testid="east-indian-chart">
        {/* Amber highlight on the ASCENDANT-rashi cell (Lagna) — drawn FIRST so borders overlay it */}
        <polygon points={RASHI_POLY[ascRashi]} fill="#FEF3C7" stroke="none" />

        {/* Outer border */}
        <rect x="2" y="2" width="296" height="296" fill="none" stroke={STROKE} strokeWidth="4" />
        {/* 3×3 grid lines */}
        <line x1="100" y1="0"   x2="100" y2="300" stroke={STROKE} strokeWidth="3" />
        <line x1="200" y1="0"   x2="200" y2="300" stroke={STROKE} strokeWidth="3" />
        <line x1="0"   y1="100" x2="300" y2="100" stroke={STROKE} strokeWidth="3" />
        <line x1="0"   y1="200" x2="300" y2="200" stroke={STROKE} strokeWidth="3" />
        {/* Corner diagonals: outer-chart-corner → inner-chart-corner (converge to centre) */}
        <line x1="0"   y1="0"   x2="100" y2="100" stroke={STROKE} strokeWidth="3" />
        <line x1="300" y1="0"   x2="200" y2="100" stroke={STROKE} strokeWidth="3" />
        <line x1="0"   y1="300" x2="100" y2="200" stroke={STROKE} strokeWidth="3" />
        <line x1="300" y1="300" x2="200" y2="200" stroke={STROKE} strokeWidth="3" />

        {/* Central "Rasi" label */}
        <text x="150" y="146" textAnchor="middle" fontSize="20" fill={STROKE}
              fontFamily="ui-serif, Georgia, serif" fontWeight="900" letterSpacing="3">
          Rasi
        </text>
        <text x="150" y="164" textAnchor="middle" fontSize="10" fill="#64748B"
              fontFamily="ui-monospace, monospace" fontWeight="700" letterSpacing="2">
          East Indian
        </text>
        <text x="150" y="182" textAnchor="middle" fontSize="11" fill="#DC2626"
              fontFamily="ui-monospace, monospace" fontWeight="900">
          {`Lagna: ${RASHI_NAMES[ascSignIndex]}`}
        </text>
        {typeof kundli.ascendant?.degrees_in_sign === "number" && (
          <text x="150" y="196" textAnchor="middle" fontSize="9" fill="#64748B"
                fontFamily="ui-monospace, monospace" fontWeight="700">
            {`${kundli.ascendant.degrees_in_sign.toFixed(2)}°`}
          </text>
        )}

        {/* 12 fixed rashi positions */}
        {Array.from({ length: 12 }, (_, i) => i + 1).map((rashi) => {
          const pos = RASHI_POS[rashi];
          const house = houseOfRashi(ascRashi, rashi);
          const planets = planetsByRashi[rashi] || [];
          const isLagna = rashi === ascRashi;
          const font = planets.length <= 2 ? 12 : planets.length <= 3 ? 10 : 9;
          const lineH = font + 1;

          return (
            <g key={rashi} data-testid={`east-rashi-${rashi}`}>
              {/* House number (dynamic) — small BLACK, top-left of cell */}
              <text x={pos.hLx} y={pos.hLy} fontSize="10" fill="#0F172A"
                    fontFamily="ui-monospace, monospace" fontWeight="900">
                {`H${house}`}
              </text>
              {/* Rashi number (fixed) — small dark, top-right of cell */}
              <text x={pos.rLx} y={pos.rLy} textAnchor="end" fontSize="10" fill={STROKE}
                    fontFamily="ui-monospace, monospace" fontWeight="900">
                {String(showRashiName ? RASHI_SHORT[rashi - 1] : rashi)}
              </text>
              {/* Lagna marker on the ascendant rashi */}
              {isLagna && (
                <text x={pos.cx} y={pos.cy + 18} textAnchor="middle" fontSize="10"
                      fill="#DC2626" fontFamily="ui-monospace, monospace" fontWeight="900">
                  ★Lg
                </text>
              )}
              {/* Planets stacked at rashi centroid */}
              {planets.map((p, i) => {
                const s = PLANET_SHORT[p.name] || p.name.slice(0, 2);
                const deg = typeof p.degrees_in_sign === "number"
                  ? ` ${p.degrees_in_sign.toFixed(0)}°` : "";
                const retro = p.retrograde ? " ℞" : "";
                return (
                  <text key={p.name} x={pos.cx}
                        y={pos.cy - ((planets.length - 1) * lineH) / 2 + i * lineH + 3}
                        textAnchor="middle" fontSize={font} fontWeight="900"
                        fill={p.retrograde ? "#B45309" : "#0F172A"}
                        fontFamily="ui-monospace, monospace">
                    {`${s}${deg}${retro}`}
                  </text>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
